import * as UUID from '../../utils/Uuid';
import { AlertRecord, AlertInfo, AlertType, AlertCategories, AlertLevel, AlertState, AlertItemType } from "../Types";
import * as Config from '../../config';
import locale, { Locale } from '../../utils/Locale';
import * as CommonText from "../../utils/common-text";
import { noValueSign } from '../../types';
import { State } from "../../status/Types";
import { ContentCategories } from "../../types";


export class AlertLogic {
    static type: AlertType | AlertType[] = [];
    static subTypes: string[] = [];
    static category = AlertCategories.OTHER;
    static contentCategories: ContentCategories[] = [];
    static title: String | String[] | Function = locale.gettext('Unknown Alert');

    /*
    static getInfo(alert: AlertRecord, allConfigs: Config.AllConfig, states: State[], timeZone?: string): AlertInfo {
        // `this` refers to the constructor of the class this method is called on.
        // This ensures that subclasses of AlertLogic instantiate themselves, not the base class.
        return (new this(alert, allConfigs, states)).getInfo();
    }
    */

    static mock(type: AlertType, config: Config.AllConfig, subType?: string): AlertRecord | AlertRecord[] {
        return [

            // Unknown uuids
            {
                id: Math.floor(Math.random() * 100000),
                siteId: Math.floor(Math.random() * 100000),
                uuid: UUID.generate(),
                type,
                bodyUuid: "body-uuid-test",
                entityUuid: "entity-uuid-test",
                controlUuids: [ "control-uuid-test" ],
                deviceId: Math.floor(Math.random() * 100000),
                data: {},
                closedBy: null,
                created: new Date(),
                updated: new Date(),
                level: AlertLevel.Error,
                state: AlertState.Open,
                itemType: AlertItemType.Device,
            },

            // Known uuids
            {
                id: Math.floor(Math.random() * 100000),
                siteId: Math.floor(Math.random() * 100000),
                uuid: UUID.generate(),
                type,
                bodyUuid: config.Config.BodiesOfWater?.[ 0 ]?.UUID,
                entityUuid: config.Config.Devices?.[ 0 ]?.UUID,
                controlUuids: [ config.Config.Controls?.[ 0 ]?.UUID, ],
                deviceId: Math.floor(Math.random() * 100000),
                data: {},
                closedBy: null,
                created: new Date(),
                updated: new Date(),
                level: AlertLevel.Error,
                state: AlertState.Open,
                itemType: AlertItemType.Device,
            }
        ];
    }

    alert: AlertRecord;
    allConfigs:Config.AllConfig;
    states: State[];
    locale: Locale;

    /**
     * 
     * @param alert 
     * @param allConfigs 
     * @param states 
     * @param customLocale Optional locale instance to use instead of the default locale.
     */
    constructor(alert: AlertRecord, allConfigs: Config.AllConfig, states: State[] = [], customLocale?: Locale) {
        this.alert = alert;
        this.allConfigs = allConfigs;
        this.states = states;
        this.locale = customLocale || locale;
    }

    /**
     * @param timeZone Override time zone specified by internal locale.
     * @returns 
     */
    getCommons(timeZone?: string): {
        body: Config.BodyOfWaterConfig,
        device: Config.DeviceConfig,
        alertData: any,
        /** Formatted date string or text: "Unknown" */
        firstReported: string,
        /** Formatted date string or text: "Never communicate" */
        lastSeen: string
    } {
        let body = { Name: 'BodyUuid: ' + (this.alert?.bodyUuid ?? noValueSign) } as Config.BodyOfWaterConfig;
        if (this.alert?.bodyUuid) {
            body = this.allConfigs?.Config?.BodiesOfWater?.find(item => item.UUID === this.alert.bodyUuid) ?? body;
        } else if (this.alert?.entityUuid) {
            body = this.allConfigs?.Config?.BodiesOfWater?.find(dev => dev.UUID === this.alert.entityUuid) ?? body;
        }

        const device = this.allConfigs?.Config?.Devices?.find(dev => dev.UUID === this.alert?.entityUuid) ?? 
            { Name: 'DeviceUUID: ' + (this.alert?.entityUuid ?? noValueSign), HardwareDevice: new Config.HardwareDeviceConfig() } as Config.DeviceConfig;

        const alertData = this.alert?.data || {};

        const firstReported = alertData?.ErrorStartTime ? this.locale.formatDateTime(new Date(alertData?.ErrorStartTime), false, undefined, timeZone) : this.locale.gettext('Unknown');

        const lastSeen = this.alert?.data?.OfflineDateTime
            ? this.locale.formatDateTime(new Date(this.alert?.data?.OfflineDateTime), false, undefined, timeZone)
            : this.locale.gettext('Never communicated')
        ;
        
        return {
            body, device, alertData, firstReported, lastSeen
        };
    }
    
    /**
     * @param timeZone Override time zone specified by internal locale.
     * @returns 
     */
    getInfo(timeZone?: string): AlertInfo {
        const alert = this.alert;

        let bodyConfig: Partial<Config.BodyOfWaterConfig> | undefined = undefined;
        let deviceConfig: Partial<Config.DeviceConfig> | undefined = undefined;
        let entityConfig: Record<string, any> | undefined = undefined;
        let featureConfig: Partial<Config.WaterFeatureConfig> | undefined = undefined;

        if (this.alert?.bodyUuid) {
            bodyConfig = this.allConfigs?.Config?.BodiesOfWater?.find(item => item.UUID === this.alert.bodyUuid) || { UUID: alert?.bodyUuid };
        }    
        if (this.alert?.entityUuid) {
            bodyConfig = this.allConfigs?.Config?.BodiesOfWater?.find(item => item.UUID === this.alert.entityUuid);
            deviceConfig = this.allConfigs?.Config?.Devices?.find(item => item.UUID === this.alert?.entityUuid);
            featureConfig = this.allConfigs?.Config?.WaterFeatures?.find(item => item.UUID === this.alert?.entityUuid);
            if (!bodyConfig && !deviceConfig && !featureConfig) {
                entityConfig = { UUID: this.alert?.entityUuid };
            }
        }
        const bodyNameOrUUID = bodyConfig?.Name ?? bodyConfig?.UUID;
        const deviceNameOrUUID = deviceConfig?.Name ?? deviceConfig?.UUID;
        const entityNameOrUUID = entityConfig?.Name ?? entityConfig?.UUID;
        const featureNameOrUUID = featureConfig?.Name ?? featureConfig?.UUID;
        
        // Each type of alert has it's own child class with getInfo() overrided.
        // So in case the getInfo() of the base class below was called, that means it is an unhandled alert.

        let title = (this.alert?.type ?? "").replaceAll("_", " ").toLocaleLowerCase();
        // Captalize first letter
        title = title.slice(0, 1).toLocaleUpperCase() + title.slice(1);
        const info = AlertInfo.fromCardConfig({
            cardTitle: this.locale.gettext("%s alert", title),
            cardDescription: [
                bodyNameOrUUID && this.locale.gettext("Body: %s", bodyNameOrUUID),
                deviceNameOrUUID && this.locale.gettext("Device: %s", deviceNameOrUUID),
                entityNameOrUUID && this.locale.gettext("Entity: %s", entityNameOrUUID),
                featureNameOrUUID && this.locale.gettext("Water Feature: %s", featureNameOrUUID),
            ]
                .filter(item => item)
                .join("\n")
                .trim()
            ,
            cardIllustration: "status_problem",
            detailDescriptionArr: [
                this.locale.gettext("There is a '%s' alert being reported by:", title),
                bodyNameOrUUID && this.locale.gettext("Body of Water: %s", bodyNameOrUUID),
                deviceNameOrUUID && this.locale.gettext("Device: %s", deviceNameOrUUID),
                entityNameOrUUID && this.locale.gettext("Entity: %s", entityNameOrUUID),
                featureNameOrUUID && this.locale.gettext("Water Feature: %s", featureNameOrUUID),
                this.locale.gettext("No additional information is available.  This can happen if your Attendant reports alerts that the mobile app hasn't been configured for yet."),
                this.locale.gettext("Check the app store to see if updates are available."),
                CommonText.pleaseCallPoolsideSupportContact,
            ].filter(item => item),
            detailIcon: "exclamation-triangle",
        });
        info.contentCategories = [ ContentCategories.SYSTEM_INFO ];
        info.created = this.alert.created;
        info.updated = this.alert.updated;
        return info;
    };
}